
import { useState } from 'react';
import { Submission } from '../lib/types';
import { Button } from '@/components/ui/button';
import { Heart, UserPlus, UserCheck, Award } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

interface PhotoCardProps {
  submission: Submission;
  isVoted: boolean;
  isWinner?: boolean;
  onVote: (submissionId: string) => void;
  showComments?: boolean;
}

export const PhotoCard: React.FC<PhotoCardProps> = ({
  submission,
  isVoted,
  isWinner = false,
  onVote,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { user, toggleFollow, isFollowing } = useAuth();
  const [likeTimeout, setLikeTimeout] = useState<number | null>(null);
  const [tapCount, setTapCount] = useState(0);

  const handleFollowClick = async () => {
    if (!user) {
      toast('Please log in to follow users');
      return;
    }
    try {
      await toggleFollow(submission.userId);
      toast(isFollowing(submission.userId)
        ? `Unfollowed @${submission.username}`
        : `Following @${submission.username}`);
    } catch {
      toast('Failed to follow user');
    }
  };

  const handleDoubleClick = () => {
    if (!user) {
      toast('Please log in to like photos');
      return;
    }
    onVote(submission.id);
  };

  // Mobile tap double click fallback
  const handlePhotoTap = () => {
    setTapCount(prev => {
      if (prev === 0) {
        // If first tap, set timeout to reset tap count
        const timeout = window.setTimeout(() => {
          setTapCount(0);
        }, 300);
        setLikeTimeout(timeout);
        return 1;
      } else {
        // Double tap detected
        if (likeTimeout) clearTimeout(likeTimeout);
        setTapCount(0);
        handleDoubleClick();
        return 0;
      }
    });
  };

  const following = user && isFollowing(submission.userId);

  return (
    <div className="flex flex-col items-center w-full">
      {/* Username + Follow: Make it compact */}
      <div className="flex items-center justify-between w-full mb-1 px-1">
        <div className="flex items-center gap-1">
          <Avatar className="h-6 w-6 ring-1 ring-bauhaus-brown/40">
            {submission.photoUrl && <AvatarImage src={submission.photoUrl} alt={submission.username} />}
            <AvatarFallback className="bg-bauhaus-offwhite text-bauhaus-darkgreen border border-bauhaus-darkgreen text-xs">
              {submission.username.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="text-xs font-semibold text-bauhaus-darkgreen leading-none truncate max-w-[85px]">
            @{submission.username}
          </span>
        </div>
        {user && user.id !== submission.userId && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleFollowClick}
            className={
              (following
                ? "border-bauhaus-brown text-bauhaus-offwhite bg-bauhaus-brown"
                : "border-bauhaus-brown text-bauhaus-brown bg-transparent") +
              " h-6 px-2 py-0 rounded text-xs min-w-0 flex items-center"
            }
            style={{ fontSize: "0.7rem", height: "1.65rem", minHeight: "0", minWidth: "0" }}
          >
            {following ? (
              <>
                <UserCheck className="h-3.5 w-3.5 mr-1 text-bauhaus-offwhite" />
                <span className="text-bauhaus-offwhite hidden sm:inline">Following</span>
              </>
            ) : (
              <>
                <UserPlus className="h-3.5 w-3.5 mr-1" />
                <span className="hidden sm:inline">Follow</span>
              </>
            )}
          </Button>
        )}
      </div>
      {/* Photo fills the card */}
      <div
        className="w-full aspect-square relative rounded-lg overflow-hidden group border border-bauhaus-green bg-bauhaus-cream"
        style={{ maxWidth: '100%', minHeight: '140px' }}
      >
        <img
          src={submission.photoUrl}
          alt={`Photo by ${submission.username}`}
          className={`object-cover w-full h-full select-none transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setIsLoaded(true)}
          draggable={false}
          style={{ userSelect: 'none' }}
          onDoubleClick={handleDoubleClick}
          onTouchEnd={handlePhotoTap}
        />
        {/* Like/Heart on hover only (desktop/tablet) */}
        <div className="absolute bottom-2 left-2 flex items-center bg-bauhaus-offwhite/80 rounded-full px-2 py-0.5 shadow opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
          <button
            aria-label="Like photo"
            className="focus:outline-none flex items-center"
            onClick={handleDoubleClick}
            disabled={!user}
          >
            <Heart className={`h-5 w-5 transition-colors ${isVoted ? 'fill-bauhaus-tan text-bauhaus-tan' : 'text-bauhaus-tan'}`} />
          </button>
        </div>
        {isWinner && (
          <div className="absolute top-2 right-2 bg-bauhaus-tan text-bauhaus-offwhite rounded-md px-1 py-1 shadow-xl animate-bounce z-20 flex items-center">
            <Award className="h-4 w-4" />
          </div>
        )}
      </div>
    </div>
  );
};
